import { useCallback, useState } from 'react'
import type { JSX } from 'react'

import { COUPON_KINDS } from '@lensmira/domain'

import { useTranslation } from '../i18n.js'
import { statusLine } from '../queue/status-line.js'
import { useWriteSettled } from '../queue/use-settled-write.js'
import { useWriteQueue } from '../queue/use-write-queue.js'
import { couponKindLabel } from '../status-labels.js'

/**
 * Issuing a coupon for one offer. §6.25 `PRM-005`.
 *
 * **The code is typed, not generated, and that is the spec's gap rather than a choice here.**
 * `packages/domain/src/promotions.ts` records that the format is unspecified: no length, alphabet,
 * checksum or case rule, and codes are compared exactly. So nothing in the product can generate one
 * without inventing a format a shop would then be stuck with, and a shop running "EID10" on a poster
 * needs to type that anyway. `later_todo.md` carries the call.
 *
 * **The two caps are hidden on a single-use coupon, because the server refuses them.** `PRM-005`
 * gives "single-use, multi-use, per-customer, per-campaign": a redemption cap on a coupon that can
 * be redeemed once is either redundant or a contradiction, and `COUPON_BAD_LIMIT` says so. Offering
 * a field whose only outcome is a refusal is worse than not offering it.
 *
 * **The code is shown back after it is issued.** There is no list or get route for coupons and no
 * id anywhere else, so the server's answer is the only place a shopkeeper will ever see what was
 * created. Losing it means issuing another.
 */

export interface IssueCouponProps {
  readonly promotionId: string
  readonly promotionLabel: string
}

interface Issued {
  readonly code?: string
  readonly kind?: string
  readonly expiresOn?: string
}

export function IssueCoupon({ promotionId, promotionLabel }: IssueCouponProps): JSX.Element {
  const { t } = useTranslation()
  const { enqueue, snapshot } = useWriteQueue()
  const [open, setOpen] = useState(false)
  const [code, setCode] = useState('')
  const [kind, setKind] = useState('single_use')
  const [startsOn, setStartsOn] = useState('')
  const [expiresOn, setExpiresOn] = useState('')
  const [maxRedemptions, setMaxRedemptions] = useState('')
  const [perCustomerLimit, setPerCustomerLimit] = useState('')
  const [campaignId, setCampaignId] = useState('')
  const [issued, setIssued] = useState<Issued | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [writeId, setWriteId] = useState<string | null>(null)

  useWriteSettled(snapshot, writeId, (write) => {
    setWriteId(null)
    setIssued(
      typeof write.response === 'object' && write.response !== null
        ? (write.response as Issued)
        : {},
    )
    setCode('')
    setOpen(false)
  })

  const issue = useCallback(async () => {
    if (code.trim() === '') {
      setError(t('coupon.codeRequired'))
      return
    }
    if (expiresOn === '') {
      // `PRM-005` tracks expiry, and the service has no default: a coupon with no last day is one
      // nobody decided to stop, which is the same leak `PRM-001`'s window closes for an offer.
      setError(t('coupon.expiryRequired'))
      return
    }
    const whole = (value: string): boolean => /^[0-9]+$/.test(value.trim())
    if (maxRedemptions.trim() !== '' && !whole(maxRedemptions)) {
      setError(t('coupon.capWhole'))
      return
    }
    if (perCustomerLimit.trim() !== '' && !whole(perCustomerLimit)) {
      setError(t('coupon.capWhole'))
      return
    }
    setError(null)
    const write = (await enqueue({
      method: 'POST',
      path: '/promotions/coupons',
      label: t('coupon.queueLabel'),
      body: {
        promotionId,
        code: code.trim(),
        kind,
        expiresOn,
        ...(startsOn === '' ? {} : { startsOn }),
        // Both caps are refused on a single-use coupon, so they are not sent for one even if a
        // number is sitting in the field from before the kind was changed.
        ...(kind === 'single_use' || maxRedemptions.trim() === ''
          ? {}
          : { maxRedemptions: Number(maxRedemptions.trim()) }),
        ...(kind === 'single_use' || perCustomerLimit.trim() === ''
          ? {}
          : { perCustomerLimit: Number(perCustomerLimit.trim()) }),
        ...(campaignId.trim() === '' ? {} : { campaignId: campaignId.trim() }),
      },
    })) as { id: string }
    setWriteId(write.id)
  }, [
    campaignId,
    code,
    enqueue,
    expiresOn,
    kind,
    maxRedemptions,
    perCustomerLimit,
    promotionId,
    startsOn,
    t,
  ])

  const line = statusLine(t, snapshot, writeId, t('queue.subject.coupon'))

  return (
    <div className="lm-coupon">
      <button
        type="button"
        className="lm-button lm-button--secondary"
        aria-expanded={open}
        onClick={() => {
          setOpen(!open)
          setError(null)
        }}
      >
        {t('coupon.issueFor', { label: promotionLabel })}
      </button>

      {issued === null ? null : (
        <p className="lm-coupon__issued" role="status">
          {/* The only place this code is ever shown: nothing lists coupons, so a lost code is a
              coupon that has to be issued again. */}
          {t('coupon.issued', { code: issued.code ?? '' })}
        </p>
      )}

      {!open ? null : (
        <div className="lm-coupon__form">
          <label className="lm-field lm-field--inline">
            <span className="lm-field__label">{t('coupon.codeLabel')}</span>
            <input
              className="lm-input"
              autoComplete="off"
              value={code}
              onChange={(e) => {
                setCode(e.currentTarget.value)
              }}
            />
          </label>
          {/* Said out loud, because a shopkeeper will reasonably expect the product to make one. */}
          <p className="lm-hint">{t('coupon.codeIsYours')}</p>

          <label className="lm-field lm-field--inline">
            <span className="lm-field__label">{t('coupon.kindLabel')}</span>
            <select
              className="lm-input"
              value={kind}
              onChange={(e) => {
                setKind(e.currentTarget.value)
              }}
            >
              {COUPON_KINDS.map((couponKind) => (
                <option key={couponKind} value={couponKind}>
                  {couponKindLabel(t, couponKind)}
                </option>
              ))}
            </select>
          </label>

          <label className="lm-field lm-field--inline">
            <span className="lm-field__label">{t('coupon.startsOnLabel')}</span>
            <input
              className="lm-input"
              type="date"
              value={startsOn}
              onChange={(e) => {
                setStartsOn(e.currentTarget.value)
              }}
            />
          </label>

          <label className="lm-field lm-field--inline">
            <span className="lm-field__label">{t('coupon.expiresOnLabel')}</span>
            <input
              className="lm-input"
              type="date"
              value={expiresOn}
              onChange={(e) => {
                setExpiresOn(e.currentTarget.value)
              }}
            />
          </label>

          {kind === 'single_use' ? (
            <p className="lm-hint">{t('coupon.singleUseMeans')}</p>
          ) : (
            <>
              <label className="lm-field lm-field--inline">
                <span className="lm-field__label">{t('coupon.maxRedemptionsLabel')}</span>
                <input
                  className="lm-input"
                  inputMode="numeric"
                  autoComplete="off"
                  value={maxRedemptions}
                  onChange={(e) => {
                    setMaxRedemptions(e.currentTarget.value)
                  }}
                />
              </label>

              <label className="lm-field lm-field--inline">
                <span className="lm-field__label">{t('coupon.perCustomerLabel')}</span>
                <input
                  className="lm-input"
                  inputMode="numeric"
                  autoComplete="off"
                  value={perCustomerLimit}
                  onChange={(e) => {
                    setPerCustomerLimit(e.currentTarget.value)
                  }}
                />
              </label>
              <p className="lm-hint">{t('coupon.capsOptional')}</p>
            </>
          )}

          <label className="lm-field lm-field--inline">
            <span className="lm-field__label">{t('coupon.campaignLabel')}</span>
            <input
              className="lm-input"
              autoComplete="off"
              value={campaignId}
              onChange={(e) => {
                setCampaignId(e.currentTarget.value)
              }}
            />
          </label>

          <button
            type="button"
            className="lm-button lm-button--primary"
            onClick={() => void issue()}
          >
            {t('coupon.issue')}
          </button>

          {error === null ? null : (
            <p className="lm-hint" role="alert">
              {error}
            </p>
          )}

          {line === null ? null : (
            <p className="lm-queue-state" role="status">
              {line}
            </p>
          )}
        </div>
      )}
    </div>
  )
}
